import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  ResponsiveContainer, ComposedChart, Bar, Rectangle, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';
import dayjs from 'dayjs';
import {
  BAR_STATE_KEYS, HOURLY_STATE_LABEL, dayCutoffMs, computeHourlyStateStacks, hourlyUtilPct, topmostBarKey,
} from './eqpHourly';

const OFFLINE_HATCH_ID = 'eqpHourlyOfflineHatch';
const EMERGENCY_HATCH_ID = 'eqpHourlyEmergencyHatch';

// 막대 색 - RUNNING 만 꽉 채운 강한 색을 쓰고 나머진 옅게 눌러서, 예전 분단위 차트처럼
// "회색(오프라인/데이터없음) 떡칠"이 전체를 덮어버리지 않게 한다.
// ALARM/EMERGENCY는 둘 다 "에러 계열"이라 색만으로는 (특히 작은 막대에서) 구분이 잘 안 됐다
// (2026-10-17 1호기 - 범례에 둘 다 "빨강"으로 보인다는 지적) - EMERGENCY는 사선 해칭을 더해 모양으로도 구분한다.
const barFillOf = (theme, key) => {
  switch (key) {
    case 'RUNNING': return theme.palette.success.main;
    case 'HOLD': return alpha(theme.palette.warning.main, 0.55);
    case 'ALARM': return theme.palette.error.main;
    case 'EMERGENCY': return `url(#${EMERGENCY_HATCH_ID})`; // 진한 빨강 + 흰 사선 해칭 - 아래 defs 참고
    case 'IDLE': return alpha(theme.palette.grey[400], 0.6);
    case 'OFFLINE': return `url(#${OFFLINE_HATCH_ID})`; // 해칭 패턴 - 아래 defs 참고
    default: return theme.palette.grey[300];
  }
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  const bucket = payload[0]?.payload;
  const util = hourlyUtilPct(bucket);
  return (
    <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, boxShadow: 1 }}>
      <Typography variant="caption" fontWeight={700}>{label}</Typography>
      {[...BAR_STATE_KEYS, 'NODATA'].map((key) => (
        bucket[key] > 0 && (
          <Typography key={key} variant="caption" sx={{ display: 'block' }}>
            {HOURLY_STATE_LABEL[key]} {Math.round(bucket[key])}분
          </Typography>
        )
      ))}
      {util != null && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }} color="success.main" fontWeight={700}>
          가동률 {util.toFixed(1)}%
        </Typography>
      )}
    </Box>
  );
};

// 스택 막대에서 맨 위 조각(그 시간대에 실제로 보이는 가장 나중 키)에만 둥근 위쪽 모서리를 준다.
// 조각마다 radius 를 주면 아래 조각의 둥근 자리에 배경색 틈이 보여서, 그 시간의 "맨 위"만 판정해 그린다.
const makeStackedShape = (theme, barKey) => (props) => {
  const { x, y, width, height, payload } = props;
  if (!height || height <= 0) return null;
  const isTop = topmostBarKey(payload) === barKey;
  return (
    <Rectangle
      x={x} y={y} width={width} height={height}
      fill={barFillOf(theme, barKey)}
      radius={isTop ? [4, 4, 0, 0] : 0}
    />
  );
};

// 시간대별 가동 현황 - 시간마다 운전/일시정지/알람/비상정지/정지/오프라인/데이터없음 분을 쌓은 막대 +
// 가동률(%) 라인. 분 단위 계단형 두 축 차트 대신, "그 시간에 뭘 하고 있었나"를 한눈에 보려고 만들었다.
const EqpHourlyStateChart = ({ intervals = [], date, isToday, zoomRange, loading }) => {
  const theme = useTheme();
  const dayStart = useMemo(() => dayjs(date).startOf('day'), [date]);

  const chartData = useMemo(() => {
    const cutoffMs = dayCutoffMs(dayStart, isToday, dayjs());
    const hours = computeHourlyStateStacks(intervals, dayStart, cutoffMs);
    const { startHour, endHour } = zoomRange || { startHour: 0, endHour: 23 };
    return hours
      .filter((h) => h.hour >= startHour && h.hour <= endHour)
      .map((h) => ({ ...h, label: `${String(h.hour).padStart(2, '0')}시`, util: hourlyUtilPct(h) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervals, dayStart, isToday, zoomRange]);

  // 범례는 실제로 데이터에 등장한 상태만 - 안 나온 상태까지 다 늘어놓으면 어차피 안 쓰는 칩만 넓게 깔린다
  const presentKeys = useMemo(
    () => BAR_STATE_KEYS.filter((key) => chartData.some((h) => h[key] > 0)),
    [chartData],
  );
  const legendPayload = useMemo(() => ([
    ...presentKeys.map((key) => ({ value: HOURLY_STATE_LABEL[key], type: 'rect', color: barFillOf(theme, key) })),
    { value: '가동률', type: 'line', color: theme.palette.primary.main },
  ]), [presentKeys, theme]);

  if (loading && chartData.every((h) => h.elapsedMin === 0)) {
    return <Typography variant="body2" color="text.secondary">불러오는 중...</Typography>;
  }

  return (
    <Box sx={{ width: '100%', height: 280 }}>
      <ResponsiveContainer>
        <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="30%">
          <defs>
            {/* 오프라인 막대 - 아주 옅은 회색 바탕에 대각선 해칭만 살짝, "꽉 찬 덩어리"로 안 보이게 */}
            <pattern id={OFFLINE_HATCH_ID} patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
              <rect width="6" height="6" fill={theme.palette.grey[200]} />
              <line x1="0" y1="0" x2="0" y2="6" stroke={theme.palette.grey[400]} strokeWidth="1" />
            </pattern>
            {/* 비상정지 - 진한 빨강 바탕에 흰 사선, ALARM(꽉 찬 밝은 빨강)과 모양으로도 구분되게 */}
            <pattern id={EMERGENCY_HATCH_ID} patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
              <rect width="6" height="6" fill={theme.palette.error.dark} />
              <line x1="0" y1="0" x2="0" y2="6" stroke="#fff" strokeWidth="1.5" />
            </pattern>
          </defs>
          <CartesianGrid stroke={theme.palette.divider} vertical={false} />
          <XAxis
            dataKey="label" tick={{ fontSize: 10 }} axisLine={false} tickLine={false}
            interval={chartData.length > 12 ? 1 : 0}
          />
          <YAxis
            yAxisId="min" domain={[0, 60]} ticks={[0, 15, 30, 45, 60]} tick={{ fontSize: 11 }}
            width={34} unit="분" axisLine={false} tickLine={false}
          />
          <YAxis
            yAxisId="util" orientation="right" domain={[0, 100]} ticks={[0, 50, 100]} tick={{ fontSize: 11 }}
            width={34} unit="%" axisLine={false} tickLine={false} label={{ value: '가동률', position: 'insideTopRight', fontSize: 10, fill: theme.palette.text.secondary }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend payload={legendPayload} wrapperStyle={{ fontSize: 11 }} />
          {BAR_STATE_KEYS.map((key) => (
            <Bar
              key={key} yAxisId="min" dataKey={key} name={HOURLY_STATE_LABEL[key]} stackId="hour"
              fill={barFillOf(theme, key)} isAnimationActive={false}
              shape={makeStackedShape(theme, key)}
              legendType="none"
            />
          ))}
          <Line
            yAxisId="util" dataKey="util" name="가동률" type="linear"
            stroke={theme.palette.primary.main} strokeWidth={2} dot={{ r: 2 }}
            isAnimationActive={false} connectNulls
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default EqpHourlyStateChart;
